import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { DocumentIcon } from 'components';
import { UPLOAD_URLS } from 'config/constants';
import uuid from 'uuid/v1';
import classnames from 'classnames';
import { uploadFile } from 'utils/files/uploadFile';
import { FilesList } from './FilesList';
import './upload.scss';

const NETWORK_ERROR_MESSAGE = 'Network error';

export class UploadFilesSimpleField extends PureComponent {
  static propTypes = {
    initial: PropTypes.array,
    uploadIcon: PropTypes.node,
    multiple: PropTypes.bool,
    accept: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    uploadURL: PropTypes.string,
    maxFilesCount: PropTypes.number,
    // allowed extensions props
    allowedExtensions: PropTypes.array, // empty if all extensions is allowed
    allowedExtensionsErrorMessage: PropTypes.string,
    // max file size props
    maxFileSize: PropTypes.number, // max MB
    maxFileSizeErrorMessage: PropTypes.string,
  };

  static defaultProps = {
    initial: [],
    uploadIcon: null,
    multiple: true,
    accept: '',
    name: '',
    onChange: () => {},
    uploadURL: UPLOAD_URLS.files,
    maxFilesCount: Infinity,
    // allowed extensions props
    allowedExtensions: [],
    allowedExtensionsErrorMessage: 'Incorrect file format',
    // max file size props
    maxFileSize: Infinity,
    maxFileSizeErrorMessage: 'File is too large',
  };

  state = {
    items: [],
    initial: [],
    dragNow: false,
  };

  dropRef = React.createRef();
  inputRef = React.createRef();
  dragCounter = 0;

  componentDidMount() {
    const dropArea = this.dropRef.current;
    dropArea.addEventListener('dragenter', this.handleDragIn);
    dropArea.addEventListener('dragleave', this.handleDragOut);
    dropArea.addEventListener('dragover', this.handleDrag);
    dropArea.addEventListener('drop', this.handleDrop);
  }

  componentWillUnmount() {
    const dropArea = this.dropRef.current;
    dropArea.removeEventListener('dragenter', this.handleDragIn);
    dropArea.removeEventListener('dragleave', this.handleDragOut);
    dropArea.removeEventListener('dragover', this.handleDrag);
    dropArea.removeEventListener('drop', this.handleDrop);
  }

  handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      this.setState({ dragNow: true });
    }
  };

  handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.setState({ dragNow: false });
    }
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropEvent = {
      target: e.dataTransfer,
    };
    this.setState({ dragNow: false });
    this.dragCounter = 0;
    this.onDrop(dropEvent);
  };

  static getDerivedStateFromProps(props, state) {
    const { initial } = props;

    if (initial !== state.initial) {
      return {
        items: (initial || []).map((initialFile) => {
          return {
            ...initialFile,
            _id: uuid(),
            status: 'COMPLETE',
          };
        }),
        initial,
      };
    }

    return state;
  }

  onDrop = (event) => {
    const { maxFilesCount } = this.props;
    const { items } = this.state;
    const droppedFiles = [...event.target.files];
    const newItems = [...items];

    droppedFiles.forEach((file) => {
      const { name, size } = file;

      let errorMessage = '';

      const { maxFileSize, maxFileSizeErrorMessage } = this.props;
      const isFileSizeAllowed = size / 1048576 <= maxFileSize;
      if (!isFileSizeAllowed) {
        errorMessage = maxFileSizeErrorMessage;
      }

      const nameArray = name.split('.');
      const extension = nameArray[nameArray.length - 1].toUpperCase();
      const { allowedExtensions, allowedExtensionsErrorMessage } = this.props;
      const isExtensionAllowed = !allowedExtensions.length
        ? true
        : Boolean(
            allowedExtensions.find(
              (allowedExtension) =>
                allowedExtension.toUpperCase() === extension,
            ),
          );
      if (!isExtensionAllowed) {
        errorMessage = allowedExtensionsErrorMessage;
      }

      const newFileItem = {
        _id: uuid(),
        fileName: name,
        fileSize: size,
        file,
        loaded: 0,
        status:
          isExtensionAllowed && isFileSizeAllowed ? 'LOAD_PENDING' : 'ERROR',
        errorMessage,
      };

      if (newItems.length >= maxFilesCount) {
        newItems.pop();
      }

      newItems.push(newFileItem);
    });

    this.setState({ items: newItems }, () => {
      this.loadFiles();
      this.outsideOnChange();
    });

    this.inputRef.current.value = '';
  };

  loadFiles = () => {
    const { maxFilesCount } = this.props;
    const { items } = this.state;

    if (items.length > maxFilesCount) {
      return;
    }

    items.forEach(async (item) => {
      if (item.status !== 'LOAD_PENDING') {
        return;
      }
      this.startLoading(item);
    });
  };

  startLoading = (item) => {
    const { uploadURL } = this.props;

    this.setState((state) => ({
      ...state,
      items: state.items.map((fileItem) => {
        const tmpItem = { ...fileItem };
        if (item._id === fileItem._id) {
          tmpItem.status = 'LOAD';
          tmpItem.loaded = 0;
          tmpItem.xhr = uploadFile(
            uploadURL,
            item.file,
            (eventStatus) => this.loadListener(eventStatus, tmpItem),
            (response) => this.completeLoading(response, tmpItem),
            () => this.errorLoading(tmpItem),
          );
        }
        return tmpItem;
      }),
    }));
  };

  loadListener = (eventStatus, item) =>
    this.setState((state) => ({
      ...state,
      items: state.items.map((fileItem) => {
        const tmpItem = { ...fileItem };
        if (item._id === fileItem._id) {
          tmpItem.loaded = eventStatus.loaded;
        }
        return tmpItem;
      }),
    }));

  completeLoading = (response = {}, item) => {
    this.setState(
      (state) => ({
        ...state,
        items: state.items.map((fileItem) => {
          const tmpItem = { ...fileItem };
          if (item._id === fileItem._id) {
            tmpItem.status = 'COMPLETE';
            tmpItem.name = response.name;
            tmpItem.keys = response.keys;
          }
          return tmpItem;
        }),
      }),
      () => {
        this.outsideOnChange();
      },
    );
  };

  errorLoading = (item) => {
    this.setState(
      (state) => ({
        ...state,
        items: state.items.map((fileItem) => {
          const tmpItem = { ...fileItem };
          if (item._id === fileItem._id) {
            tmpItem.status = 'ERROR';
            tmpItem.errorMessage = NETWORK_ERROR_MESSAGE;
          }
          return tmpItem;
        }),
      }),
      () => {
        this.outsideOnChange();
      },
    );
  };

  deleteItem = (item) => {
    this.setState(
      (state) => ({
        ...state,
        items: state.items.filter((fileItem) => {
          if (item._id === fileItem._id && fileItem.xhr) {
            fileItem.xhr.abort();
          }
          return item._id !== fileItem._id;
        }),
      }),
      () => {
        this.loadFiles();
        this.outsideOnChange();
      },
    );
  };

  outsideOnChange = () => {
    const { onChange } = this.props;
    const { items } = this.state;
    onChange(
      (items || []).map((file) => {
        const { id, fileName, fileSize, status, name, keys, urls } = file;
        return {
          id,
          fileName,
          fileSize,
          status,
          name,
          keys,
          urls,
        };
      }),
    );
  };

  render() {
    const { label, uploadIcon, multiple, accept, name } = this.props;
    const { items = [], dragNow } = this.state;

    return (
      <div
        ref={this.dropRef}
        className={classnames('upload-component-wrapper', {
          '_drag-now': dragNow,
        })}
      >
        <label className="upload-component">
          <input
            id={`upload-files-${name}`}
            type="file"
            multiple={multiple}
            accept={accept}
            name={name}
            hidden
            onChange={this.onDrop}
            ref={this.inputRef}
          />
          <div className="upload-component__icon">
            {uploadIcon || <DocumentIcon />}
          </div>
          <p className="upload-component__label-wrapper">{label}</p>
        </label>
        <FilesList
          items={items}
          deleteItem={this.deleteItem}
          reloadItem={this.startLoading}
          networkErrorMessage={NETWORK_ERROR_MESSAGE}
        />
      </div>
    );
  }
}
